

import type { AutoCaptureType, TrackingEventType, EventProperties } from '../types';

export interface AutoCaptureOptions {
  features: AutoCaptureType[];
  onEvent: (type: TrackingEventType, properties?: EventProperties) => void;
}

export class AutoCapture {
  private options: AutoCaptureOptions;
  private isRunning = false;

  
  private lastPath: string | null = null;

  
  private originalPushState: typeof history.pushState | null = null;
  private originalReplaceState: typeof history.replaceState | null = null;
  private popstateHandler: (() => void) | null = null;
  private beforeUnloadHandler: (() => void) | null = null;

  
  private maxScrollDepth = 0;
  private scrollHandler: (() => void) | null = null;
  private scrollThrottleTimer: ReturnType<typeof setTimeout> | null = null;

  
  private pageStartTime: number | null = null;
  private timeInterval: ReturnType<typeof setInterval> | null = null;
  private visibilityHandler: (() => void) | null = null;
  private isPageVisible = true;
  private totalVisibleTime = 0;

  constructor(options: AutoCaptureOptions) {
    this.options = options;
  }

  
  start(): void {
    if (this.isRunning || typeof window === 'undefined') return;

    this.isRunning = true;

    
    if (this.options.features.includes('pageView')) {
      this.setupPageViewTracking();
    }

    if (this.options.features.includes('scrollDepth')) {
      this.setupScrollTracking();
    }

    if (this.options.features.includes('timeOnPage')) {
      this.setupTimeTracking();
    }
  }

  
  stop(): void {
    this.isRunning = false;

    
    if (this.options.features.includes('timeOnPage')) {
      this.sendTimeOnPage();
    }

    
    if (this.options.features.includes('scrollDepth')) {
      this.sendScrollDepth();
    }

    
    this.cleanup();
  }

  
  private cleanup(): void {
    
    if (this.originalPushState) {
      history.pushState = this.originalPushState;
      this.originalPushState = null;
    }
    if (this.originalReplaceState) {
      history.replaceState = this.originalReplaceState;
      this.originalReplaceState = null;
    }

    
    if (this.popstateHandler) {
      window.removeEventListener('popstate', this.popstateHandler);
      this.popstateHandler = null;
    }

    
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }

    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }

    if (this.scrollThrottleTimer) {
      clearTimeout(this.scrollThrottleTimer);
      this.scrollThrottleTimer = null;
    }

    if (this.timeInterval) {
      clearInterval(this.timeInterval);
      this.timeInterval = null;
    }

    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  

  private setupPageViewTracking(): void {
    
    this.trackPageView();

    
    this.setupHistoryTracking();
  }

  private trackPageView(): void {
    const currentPath = window.location.pathname + window.location.search;

    
    if (currentPath === this.lastPath) return;

    this.lastPath = currentPath;

    
    this.maxScrollDepth = 0;
    this.totalVisibleTime = 0;
    this.pageStartTime = Date.now();

    this.options.onEvent('sdk_page_view', {
      url: window.location.href,
      path: window.location.pathname,
      title: document.title,
      referrer: document.referrer
    });
  }

  private setupHistoryTracking(): void {
    
    if (this.originalPushState) return;

    
    this.originalPushState = history.pushState;
    this.originalReplaceState = history.replaceState;

    const self = this;
    const origPush = this.originalPushState;
    const origReplace = this.originalReplaceState;

    history.pushState = function (...args) {
      origPush.apply(history, args);
      self.onHistoryChange();
    };

    history.replaceState = function (...args) {
      origReplace.apply(history, args);
      self.onHistoryChange();
    };

    
    this.popstateHandler = () => this.onHistoryChange();
    window.addEventListener('popstate', this.popstateHandler);
  }

  private onHistoryChange(): void {
    
    setTimeout(() => {
      
      if (this.options.features.includes('timeOnPage')) {
        this.sendTimeOnPage();
      }
      if (this.options.features.includes('scrollDepth')) {
        this.sendScrollDepth();
      }

      
      this.trackPageView();
    }, 50);
  }

  

  private setupScrollTracking(): void {
    this.scrollHandler = () => this.onScroll();
    window.addEventListener('scroll', this.scrollHandler, { passive: true });

    
    this.setupBeforeUnloadHandler();
  }

  private onScroll(): void {
    
    if (this.scrollThrottleTimer) return;

    this.scrollThrottleTimer = setTimeout(() => {
      this.scrollThrottleTimer = null;
      this.calculateScrollDepth();
    }, 100);
  }

  private calculateScrollDepth(): void {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = document.documentElement.clientHeight;

    
    const scrollableHeight = scrollHeight - clientHeight;
    if (scrollableHeight <= 0) {
      this.maxScrollDepth = 100;
      return;
    }

    const currentDepth = Math.round((scrollTop / scrollableHeight) * 100);
    this.maxScrollDepth = Math.max(this.maxScrollDepth, Math.min(currentDepth, 100));
  }

  private sendScrollDepth(): void {
    if (this.maxScrollDepth > 0) {
      this.options.onEvent('sdk_scroll_depth', {
        scrollDepth: this.maxScrollDepth,
        url: window.location.href,
        path: window.location.pathname
      });
    }
  }

  

  
  private setupBeforeUnloadHandler(): void {
    if (this.beforeUnloadHandler) return;
    this.beforeUnloadHandler = () => {
      this.sendScrollDepth();
      this.sendTimeOnPage();
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  private setupTimeTracking(): void {
    this.pageStartTime = Date.now();

    
    this.visibilityHandler = () => this.onVisibilityChange();
    document.addEventListener('visibilitychange', this.visibilityHandler);

    
    this.timeInterval = setInterval(() => {
      if (this.isPageVisible) {
        this.updateVisibleTime();
      }
    }, 30000);

    
    this.setupBeforeUnloadHandler();
  }

  private onVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      
      this.updateVisibleTime();
      this.isPageVisible = false;
    } else {
      
      this.pageStartTime = Date.now();
      this.isPageVisible = true;
    }
  }

  private updateVisibleTime(): void {
    if (this.pageStartTime && this.isPageVisible) {
      const elapsed = (Date.now() - this.pageStartTime) / 1000;
      this.totalVisibleTime += elapsed;
      this.pageStartTime = Date.now();
    }
  }

  private sendTimeOnPage(): void {
    
    this.updateVisibleTime();

    if (this.totalVisibleTime > 1) {
      
      this.options.onEvent('sdk_time_on_page', {
        timeOnPage: Math.round(this.totalVisibleTime),
        url: window.location.href,
        path: window.location.pathname
      });
    }
  }
}
